/**
 * Community restriction report — drawing tool + form panel for the web map.
 *
 * Flow:
 *  1. User clicks floating "Report" button
 *  2. Drawing toolbar appears: choose Polygon or Circle, draw on map
 *  3. After shape is finished a bottom-sheet form slides up
 *  4. User picks report type, adds comment / ordinance URL / photos (≤10)
 *  5. POST /api/community/report  →  POST photos one-by-one
 */

import Draw from 'ol/interaction/Draw.js';
import { fromCircle } from 'ol/geom/Polygon.js';
import GeoJSON from 'ol/format/GeoJSON.js';
import VectorSource from 'ol/source/Vector.js';
import VectorLayer from 'ol/layer/Vector.js';
import { Style, Fill, Stroke } from 'ol/style.js';

const API_BASE = `/${typeof lang !== 'undefined' ? lang : 'en'}/api`;

const REPORT_TYPES = [
  { value: 'sign',           label: 'Restriction sign / notice' },
  { value: 'seasonal',       label: 'Seasonal restriction' },
  { value: 'municipal_ban',  label: 'Municipal ban' },
  { value: 'breeding_area',  label: 'Breeding area' },
  { value: 'wild_rest_zone', label: 'Wildlife rest zone' },
  { value: 'ranger_hint',    label: 'Ranger / authority hint' },
  { value: 'conflict',       label: 'Conflict / incident' },
];

const DRAW_STYLE = new Style({
  fill: new Fill({ color: 'rgba(255,140,0,0.15)' }),
  stroke: new Stroke({ color: 'rgba(255,140,0,0.9)', width: 2, lineDash: [7, 4] }),
});

/* ─── public entry point ─────────────────────────────────────────────────── */

export function initCommunityReport(map) {
  const drawSource = new VectorSource();
  const drawLayer  = new VectorLayer({ source: drawSource, style: DRAW_STYLE, zIndex: 200 });
  map.addLayer(drawLayer);

  const fmt = new GeoJSON({
    featureProjection: map.getView().getProjection(),
    dataProjection: 'EPSG:4326',
  });

  // shared mutable state
  const state = { drawInteraction: null, geometry: null, photos: [], mode: 'polygon' };

  const mapEl = map.getTargetElement();

  // ── build DOM ──────────────────────────────────────────────────────────
  const triggerBtn = buildTriggerButton();
  const toolbar    = buildToolbar();
  const sheet      = buildSheet();
  mapEl.appendChild(triggerBtn);
  mapEl.appendChild(toolbar);
  mapEl.appendChild(sheet);

  // ── trigger button ─────────────────────────────────────────────────────
  triggerBtn.addEventListener('click', () => {
    triggerBtn.style.display = 'none';
    toolbar.style.display = 'flex';
    startDraw('polygon');
  });

  // ── toolbar buttons ────────────────────────────────────────────────────
  toolbar.querySelector('#cr-btn-polygon').addEventListener('click', () => {
    stopDraw();
    startDraw('polygon');
  });
  toolbar.querySelector('#cr-btn-circle').addEventListener('click', () => {
    stopDraw();
    startDraw('circle');
  });
  toolbar.querySelector('#cr-btn-cancel').addEventListener('click', reset);

  // ── form buttons ───────────────────────────────────────────────────────
  sheet.querySelector('#cr-btn-back').addEventListener('click', () => {
    hideSheet();
    drawSource.clear();
    state.geometry = null;
    toolbar.style.display = 'flex';
    startDraw(state.mode);
  });

  sheet.querySelector('#cr-btn-dismiss').addEventListener('click', reset);

  sheet.querySelector('#cr-photo-input').addEventListener('change', (e) => {
    const files = Array.from(e.target.files);
    const remaining = 10 - state.photos.length;
    files.slice(0, remaining).forEach(f => addPhoto(f, state, sheet));
    e.target.value = '';
  });

  sheet.querySelector('#cr-form').addEventListener('submit', async (e) => {
    e.preventDefault();
    await submitReport(state, fmt, sheet, reset);
  });

  // ── helpers ─────────────────────────────────────────────────────────
  function startDraw(mode) {
    state.mode = mode;
    updateToolbarHint(toolbar, mode);

    const olType = mode === 'circle' ? 'Circle' : 'Polygon';
    state.drawInteraction = new Draw({ source: drawSource, type: olType, style: DRAW_STYLE });

    state.drawInteraction.on('drawend', (evt) => {
      stopDraw();
      let geom = evt.feature.getGeometry();
      if (geom.getType() === 'Circle') geom = fromCircle(geom, 64);
      state.geometry = fmt.writeGeometryObject(geom);
      toolbar.style.display = 'none';
      showSheet(sheet, state);
    });

    map.addInteraction(state.drawInteraction);
  }

  function stopDraw() {
    if (state.drawInteraction) {
      map.removeInteraction(state.drawInteraction);
      state.drawInteraction = null;
    }
  }

  function reset() {
    stopDraw();
    drawSource.clear();
    state.geometry = null;
    state.photos   = [];
    hideSheet();
    resetForm(sheet);
    toolbar.style.display = 'none';
    triggerBtn.style.display = '';
  }
}

/* ─── DOM builders ─────────────────────────────────────────────────────── */

function buildTriggerButton() {
  const btn = document.createElement('button');
  btn.id = 'cr-trigger';
  btn.className = 'cr-trigger-btn';
  btn.title = 'Report a drone restriction zone';
  btn.innerHTML = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="18" height="18"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg><span>Report drone restriction zones</span>`;
  return btn;
}

function buildToolbar() {
  const el = document.createElement('div');
  el.id = 'cr-toolbar';
  el.className = 'cr-toolbar';
  el.style.display = 'none';
  el.innerHTML = `
    <span id="cr-toolbar-hint" class="cr-toolbar-hint"></span>
    <div class="cr-toolbar-btns">
      <button id="cr-btn-polygon" class="cr-tool-btn cr-tool-active" title="Draw polygon">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="16" height="16"><polygon points="12 2 22 20 2 20"/></svg> Polygon
      </button>
      <button id="cr-btn-circle" class="cr-tool-btn" title="Draw circle">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="16" height="16"><circle cx="12" cy="12" r="10"/></svg> Circle
      </button>
      <button id="cr-btn-cancel" class="cr-tool-btn cr-tool-cancel">✕ Cancel</button>
    </div>
  `;
  return el;
}

function buildSheet() {
  const el = document.createElement('div');
  el.id = 'cr-sheet';
  el.className = 'cr-sheet';
  el.style.display = 'none';

  const typeOptions = REPORT_TYPES.map(t =>
    `<option value="${t.value}">${t.label}</option>`
  ).join('');

  el.innerHTML = `
    <div class="cr-sheet-header">
      <span class="cr-sheet-title">Report a Drone Restriction Zone</span>
      <button id="cr-btn-dismiss" class="cr-sheet-close" title="Cancel">✕</button>
    </div>
    <form id="cr-form" class="cr-form" novalidate>
      <p class="cr-intro">Help other pilots by documenting restriction zones. Please describe what you observed and add at least one photo if possible.</p>
      <label class="cr-label">Type <span class="cr-req">*</span>
        <select id="cr-type" name="report_type" class="cr-select" required>
          ${typeOptions}
        </select>
      </label>
      <label class="cr-label">Description <span class="cr-req">*</span>
        <textarea id="cr-comment" name="comment" class="cr-textarea" rows="4" placeholder="Describe what you observed — e.g. 'No-fly sign at trail entrance, valid April–July. Warden confirmed drone ban during breeding season.'" required></textarea>
      </label>
      <label class="cr-label">Ordinance / source URL <span class="cr-hint">(link to official regulation if available)</span>
        <input id="cr-url" name="ordinance_url" type="url" class="cr-input" placeholder="https://…">
      </label>
      <div class="cr-label">Photos <span class="cr-hint">(optional — up to 10, max 10 MB each)</span>
        <p class="cr-photo-hint">Optional but helpful: photos of signs, notices or the restricted area help moderators verify your report faster.</p>
        <div id="cr-photo-grid" class="cr-photo-grid"></div>
        <label id="cr-photo-add" class="cr-photo-add-btn">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="18" height="18"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="12" cy="12" r="3"/><line x1="16" y1="3" x2="16" y2="7"/></svg>
          Add photos
          <input id="cr-photo-input" type="file" accept="image/jpeg,image/png,image/webp,image/heic" multiple style="display:none">
        </label>
      </div>
      <div id="cr-error" class="cr-error" style="display:none"></div>
      <div class="cr-form-footer">
        <button type="button" id="cr-btn-back" class="cr-btn-secondary">← Redraw</button>
        <button type="submit" id="cr-btn-submit" class="cr-btn-primary">Submit report</button>
      </div>
    </form>
    <div id="cr-success" class="cr-success" style="display:none">
      <div class="cr-success-icon">✓</div>
      <div class="cr-success-title">Thank you!</div>
      <div class="cr-success-body">Your report has been submitted and will be reviewed by our moderators.</div>
      <button id="cr-btn-done" class="cr-btn-primary" style="margin-top:16px">Close</button>
    </div>
  `;

  // done button wires up inside showSheet after the sheet is appended
  el.querySelector('#cr-btn-done').addEventListener('click', () => {
    el.style.display = 'none';
    resetForm(el);
    document.getElementById('cr-trigger').style.display = '';
  });

  return el;
}

/* ─── UI helpers ───────────────────────────────────────────────────────── */

function updateToolbarHint(toolbar, mode) {
  const hint = toolbar.querySelector('#cr-toolbar-hint');
  hint.textContent = mode === 'polygon'
    ? 'Click to add points • double-click to finish'
    : 'Click centre • drag to set radius';
  toolbar.querySelector('#cr-btn-polygon').classList.toggle('cr-tool-active', mode === 'polygon');
  toolbar.querySelector('#cr-btn-circle').classList.toggle('cr-tool-active', mode === 'circle');
}

function showSheet(sheet) {
  sheet.querySelector('#cr-form').style.display = '';
  sheet.querySelector('#cr-success').style.display = 'none';
  sheet.style.display = 'flex';
}

function hideSheet() {
  const sheet = document.getElementById('cr-sheet');
  if (sheet) sheet.style.display = 'none';
}

function resetForm(sheet) {
  const form = sheet.querySelector('#cr-form');
  if (form) form.reset();
  const grid = sheet.querySelector('#cr-photo-grid');
  if (grid) grid.innerHTML = '';
  const err = sheet.querySelector('#cr-error');
  if (err) { err.style.display = 'none'; err.textContent = ''; }
  sheet.querySelector('#cr-form').style.display = '';
  sheet.querySelector('#cr-success').style.display = 'none';
}

function addPhoto(file, state, sheet) {
  if (state.photos.length >= 10) return;
  state.photos.push(file);
  const grid = sheet.querySelector('#cr-photo-grid');
  const idx  = state.photos.length - 1;

  const wrapper = document.createElement('div');
  wrapper.className = 'cr-photo-thumb';
  wrapper.dataset.idx = idx;

  const img = document.createElement('img');
  img.src = URL.createObjectURL(file);
  img.onload = () => URL.revokeObjectURL(img.src);

  const del = document.createElement('button');
  del.className = 'cr-photo-del';
  del.textContent = '×';
  del.type = 'button';
  del.addEventListener('click', () => {
    state.photos.splice(idx, 1);
    wrapper.remove();
    // reindex remaining
    grid.querySelectorAll('.cr-photo-thumb').forEach((el, i) => { el.dataset.idx = i; });
    sheet.querySelector('#cr-photo-add').style.display = state.photos.length < 10 ? '' : 'none';
  });

  wrapper.appendChild(img);
  wrapper.appendChild(del);
  grid.appendChild(wrapper);
  sheet.querySelector('#cr-photo-add').style.display = state.photos.length < 10 ? '' : 'none';
}

/* ─── API calls ────────────────────────────────────────────────────────── */

async function submitReport(state, fmt, sheet, reset) {
  const form = sheet.querySelector('#cr-form');
  const errEl = sheet.querySelector('#cr-error');
  const submitBtn = sheet.querySelector('#cr-btn-submit');

  errEl.style.display = 'none';

  const comment = form.querySelector('#cr-comment').value.trim();
  if (!comment) {
    errEl.textContent = 'Please add a description before submitting.';
    errEl.style.display = '';
    form.querySelector('#cr-comment').focus();
    return;
  }

  submitBtn.disabled = true;
  submitBtn.textContent = 'Submitting…';

  try {
    const body = {
      report_type:   form.querySelector('#cr-type').value,
      geometry_type: 'polygon',
      geometry:      state.geometry,
      comment:       form.querySelector('#cr-comment').value.trim(),
      ordinance_url: form.querySelector('#cr-url').value.trim(),
    };

    const resp = await fetch(`${API_BASE}/community/report`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });

    if (!resp.ok) {
      const err = await resp.json().catch(() => ({}));
      throw new Error(err.error || `Server error ${resp.status}`);
    }

    const result = await resp.json();
    const reportId = result.id;

    // upload photos sequentially
    for (const photo of state.photos) {
      const fd = new FormData();
      fd.append('photo', photo);
      await fetch(`${API_BASE}/community/report/${reportId}/photo`, {
        method: 'POST',
        body: fd,
      }).catch(() => {}); // best-effort — don't fail the whole report
    }

    form.style.display = 'none';
    sheet.querySelector('#cr-success').style.display = 'flex';
    state.photos = [];

  } catch (err) {
    errEl.textContent = `Could not submit: ${err.message}`;
    errEl.style.display = '';
  } finally {
    submitBtn.disabled = false;
    submitBtn.textContent = 'Submit report';
  }
}
